import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Download, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/seoApi";
import { useLang } from "@/lib/i18n";

const T = {
  en: { copy: "Copy", copied: "Copied", download: "Download", toastCopy: "Copied to clipboard", toastDl: (f) => `Downloaded ${f}` },
  fr: { copy: "Copier", copied: "Copié", download: "Télécharger", toastCopy: "Copié dans le presse-papiers", toastDl: (f) => `${f} téléchargé` },
};

export const CodeCard = ({ title, language, code, filename, testIdPrefix = "code", maxHeight = "420px" }) => {
  const [copied, setCopied] = useState(false);
  const { lang } = useLang();
  const t = T[lang];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(t.toastCopy);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleDownload = () => {
    downloadFile(code, filename || "output.txt");
    toast.success(t.toastDl(filename));
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{title}</span>
          {language && <Badge variant="secondary" className="text-xs shrink-0">{language}</Badge>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" onClick={handleCopy} data-testid={`${testIdPrefix}-copy-button`} aria-label="Copy code">
            {copied ? <Check className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
            {copied ? t.copied : t.copy}
          </Button>
          {filename && (
            <Button variant="outline" size="sm" onClick={handleDownload} data-testid={`${testIdPrefix}-download-button`} aria-label="Download file">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {t.download}
            </Button>
          )}
        </div>
      </div>
      <pre
        className="code-scroll font-mono text-xs sm:text-sm leading-6 px-4 py-4 overflow-auto whitespace-pre-wrap break-words"
        style={{ maxHeight }}
        data-testid={`${testIdPrefix}-content`}
      >
        {code}
      </pre>
    </div>
  );
};

export default CodeCard;
